<script setup lang="ts">
import { computed } from 'vue';

import PresenterValueFallback from '@satellite/components/elements/presenters/PresenterValueFallback.vue';
import YardAreaPresenterV2 from '@satellite/components/elements/presenters/YardAreaPresenterV2.vue';

import type { PresenterDisplaySize } from '@satellite/composables/presenterDisplayOptions';
import type {
  YardSpotDisplayField,
  YardSpotDisplayData
} from '@satellite/composables/useYardSpotDisplay';

const {
  data,
  size,
  displayFields = ['area', 'code', 'spotIcon']
} = defineProps<{
  data: YardSpotDisplayData;
  size?: PresenterDisplaySize;
  displayFields?: YardSpotDisplayField[];
}>();

const resolvedSize = computed(() => size ?? data.size);

const showArea = computed(() => displayFields.includes('area'));
const showCode = computed(() => displayFields.includes('code'));
const showSpotIcon = computed(() => displayFields.includes('spotIcon'));
const showSeparator = computed(() => showArea.value && showCode.value);
const showSpotStatus = computed(() => displayFields.includes('spotStatus'));
</script>

<template>
  <div
    class="d-flex flex-row gap-1 align-center yard-spot-presenter yard-spot-display-v2"
    :class="{
      'typography-body-sm': resolvedSize === 'small',
      'typography-body-md': resolvedSize === 'regular',
      'typography-heading-lg-bold': resolvedSize === 'large'
    }">
    <template v-if="showArea">
      <slot
        name="area"
        :area="data.area"
        :area-id="data.areaId"
        :area-name="data.areaName"
        :area-name-attrs="data.areaNameAttrs">
        <yard-area-presenter-v2
          v-if="data.areaId && !data.area"
          :area-id="data.areaId"
          :size="resolvedSize"
          :mode="data.mode"
          v-bind="data.areaPresenterProps"
          :name-props="data.areaNameAttrs" />
        <presenter-value-fallback
          v-else-if="!data.areaName"
          data-testid="yard-area-presenter-area-name" />
        <span v-else v-bind="data.areaNameAttrs">
          {{ data.areaName }}
        </span>
      </slot>
    </template>
    <template v-if="showSeparator">-</template>
    <template v-if="showCode">
      <slot
        name="spot-code"
        :spot-code="data.code"
        :code="data.code"
        :spot-code-attrs="data.codeAttrs"
        :code-attrs="data.codeAttrs">
        <presenter-value-fallback v-if="!data.code" data-testid="yard-spot-presenter-spot-code" />
        <span v-else v-bind="data.codeAttrs">
          {{ data.code }}
        </span>
      </slot>
    </template>

    <template v-if="showSpotIcon">
      <slot name="spot-icon" :spot-icon="data.spotIcon" :spot-icon-attrs="data.spotIconAttrs">
        <v-icon v-bind="data.spotIconAttrs" />
      </slot>
    </template>

    <template v-if="showSpotStatus">
      <slot
        name="spot-status"
        :spot-status="data.spotStatus"
        :spot-status-attrs="data.spotStatusAttrs">
        <span v-bind="data.spotStatusAttrs">
          {{ data.spotStatus }}
        </span>
      </slot>
    </template>

    <slot name="default" :spot="data.spot" />
  </div>
</template>
